import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { StyleServiceService } from '@app/finxone-web-frontend/app/lib/services/style-service/style-service.service';
import { getSpecificWidgetPropertyStyle } from '@app/finxone-web-frontend/app/lib/utils/config.utils';
import { filterIfHandlebarNotRendered } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  ActionConfig,
  Columns,
  ProviderClass,
  SKELETON_LAYOUT_CONFIG_PATH,
  SkeletonLayoutConfig,
  UiZoneWidgetAttributeConfig,
  WidgetPropertiesType,
  ZoneActionOptions,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import Handlebars from 'handlebars';
import { Subject } from 'rxjs';
import { BaseComponent } from '../widgets/base/base.component';
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-desktop-listing',
  templateUrl: './desktop-listing.component.html',
})
export class DesktopListingComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() totalListDisabled = false;
  @Input() entityData: any[] = [];
  @Input() actionOptions: ZoneActionOptions[];
  @Input() dataIn: ProviderClass<UiZoneWidgetAttributeConfig>;
  @Input() pageNumber: number;
  @Input() isShowMoreOption = false;
  @Input() entityName: string;
  @Input() isShowOption = false;
  @Input() totalListCount: number;
  @Input() isSelectedId: string;
  @Input() isClickMenuIcon = false;
  @Input() multipleSelectionAllowed = false;
  @Input() menuPopupCss: object;
  @Input() emptyTableMsg = ' No Data Found';
  @Input() threeDotStyles: object;
  @Input() menuItemsStatus: {
    [key: string]: { [key: string]: boolean };
  };
  @Output() handleScroll = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() searchValueText = new EventEmitter<string>();
  @Output() performAction = new EventEmitter<{
    actionKey: string;
    actionId: string;
    actionOption: ZoneActionOptions;
  }>();
  @Output() actionEvent = new EventEmitter<{ actionConfig: ActionConfig; data: any }>();
  @Input() public uniqueId = 'id';
  @Output() checkboxSelection = new EventEmitter<string[]>();
  @Output() tableHeaderCheckboxSelection = new EventEmitter<any[]>();
  @Output() rowData = new EventEmitter<any>();
  @Output() dropDownValue = new EventEmitter<string>();
  @Output() confirmButtonAction = new EventEmitter<string>();
  @Input() actionButtonContent = '';
  @Input() confirmButtonStyle = {};
  @Input() actionDropdownStyle = {};
  @Input() dropdownOptions: { label: string; value: string }[];
  @Input() enableClickableRow = false;
  @Input() isApiInProgress = false;
  @Input() fetchingContent = '';
  @Input() showConfirmButton = false;
  @Input() selectedOption: { label: string; value: string };
  @Input() dropdownAction = {
    isAllowDropdownAction: false,
    dropdownLabel: '',
    dropdownWidth: 25,
  };

  public textStyle: baseCssConfigStyle;
  public skeletonConfig?: SkeletonLayoutConfig;
  public columns: Columns[] = [];
  public viewListOption: string;
  public enableSearch = true;
  public searchPlaceHolder = 'search...';
  public searchIcon: string;
  public currentActiveStyle: baseCssConfigStyle;
  public currentInactiveStyle: baseCssConfigStyle;
  public currentSearchInputStyle: baseCssConfigStyle;
  public searchIconStyle: baseCssConfigStyle;
  public viewPaginationOptions: string[] = [];
  public selectedTableSize = 'p-datatable-lg';
  public headerTextColor: string;
  public headerBackgroundColor: string;
  public tableHeaderStyles: baseCssConfigStyle;
  public gridLinesEnabled = false;
  public tableGridLines: baseCssConfigStyle;
  public tableRowSetting: baseCssConfigStyle;
  public checkboxInactiveStyle: baseCssConfigStyle;
  public checkboxCurrentStyle: baseCssConfigStyle;
  public checkboxActiveStyle: baseCssConfigStyle;
  checkboxStyles: {
    checkboxCurrentStyle: baseCssConfigStyle;
    checkboxInactiveStyle: baseCssConfigStyle;
    checkboxActiveStyle: baseCssConfigStyle;
  };
  public tableHeaderCustomCss = '';
  public tableRowCustomCss = '';
  public tableRowBgColour: {
    selectRowBackgroundStyle: string;
    oddRowBgColor: string;
    evenRowBgColor: string;
  };
  public searchInputStyle: { width: string };
  public tableToolbarHeaderStyle: { 'background-color': string; 'justify-content': string };

  constructor(
    private configService: ConfigService,
    private styleService: StyleServiceService,
  ) {
    super();
  }

  ngOnInit() {
    if (this.dataIn != undefined && this.dataIn != null) {
      this.attri = this.dataIn.attri;
    }
    if (this.dataIn?.widgetConfig?.attributes?.['columns']) {
      this.columns = this.dataIn.widgetConfig.attributes['columns'].filter((x: Columns) => x.selected);
      this.viewListOption = this.dataIn.attri?.['selectedListOption'];
    }
    this.enableSearch = this.dataIn.attri?.widgetProperties.inputFieldSettings?.isAllowed ?? true;
    this.searchPlaceHolder = this.dataIn.attri?.widgetProperties.inputFieldSettings?.label ?? '';
    this.searchIcon = this.dataIn.attri?.widgetProperties?.icon ?? '';
    this.viewPaginationOptions = this.dataIn?.attri?.['viewPaginationOption'] ?? ['5', '10', '20'];
    this.selectedTableSize = this.dataIn?.attri?.['viewTableSize'] ?? 'p-datatable-sm';
    this.gridLinesEnabled = this.dataIn?.attri?.['tableGridSetting']?.showGridLines;
    this.multipleSelectionAllowed = this.dataIn.attri?.['tableCheckboxSettings']?.enableCheckbox ?? false;
    this.tableHeaderCustomCss =
      this.dataIn?.attri?.['tableHeaderCustomCss'] ??
      'font-size: 16px; font-weight: 700; color:var(--color-on-surface-muted); padding: 15px 10px 15px 24px;';
    this.tableRowCustomCss =
      this.dataIn?.attri?.['tableRowCustomCss'] ??
      `padding: 1.6rem 2.4rem; font-size: 16px; font-weight: 400;`;
    const noRecordsTemplate = Handlebars.compile(this.attri?.['noRecordsTemplate'] ?? '');
    this.emptyTableMsg = filterIfHandlebarNotRendered(noRecordsTemplate({}));
    const fetchingTemplate = Handlebars.compile(this.attri?.['fetchingTemplate'] ?? '');
    this.fetchingContent = filterIfHandlebarNotRendered(fetchingTemplate({}));
    this.skeletonConfig = this.attri?.[SKELETON_LAYOUT_CONFIG_PATH];

    this.setCssAttributes();
    this.searchInputStyles();
    this.tableRowSettingCssConfig();
    this.tableHeaderCssAttributes();
    this.gridSettingStyleConfig();
    this.tableCheckboxCssConfig();
    this.dropdownActionConfig();
  }

  private dropdownActionConfig() {
    this.dropdownAction =
      this.dataIn.attri.widgetProperties?.['dropdownActionSetting'] ?? this.dropdownAction;
  }

  private tableHeaderCssAttributes() {
    this.tableHeaderStyles = getSpecificWidgetPropertyStyle(this.attri, [
      WidgetPropertiesType.TABLE_HEADER_CONFIG,
    ]);
  }

  private gridSettingStyleConfig() {
    this.tableGridLines = getSpecificWidgetPropertyStyle(this.attri, [
      WidgetPropertiesType.TABLE_GRID_SETTING,
    ]);
  }
  private tableRowSettingCssConfig() {
    if (this.attri?.['selectRowBackgroundStyle'] !== 'alternate-colours') {
      this.tableRowSetting = getSpecificWidgetPropertyStyle(this.attri, [
        WidgetPropertiesType.TABLE_ROW_SETTING,
      ]);
    } else {
      this.tableRowBgColour = {
        selectRowBackgroundStyle: this.attri?.['selectRowBackgroundStyle'] ?? 'single-colour',
        oddRowBgColor: this.attri['rowSettings']?.oddRowBgColor,
        evenRowBgColor: this.attri['rowSettings']?.evenRowBgColor,
      };
    }
  }

  private tableCheckboxCssConfig() {
    const { iconStyle, baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.attri?.['tableCheckboxSettings'],
    );
    this.checkboxInactiveStyle = {
      ...inactiveStyle,
      ...activeStyle,
      ...iconStyle,
    };
    this.checkboxActiveStyle = {
      ...activeStyle,
      ...baseStyle,
      ...iconStyle,
    };
    this.checkboxCurrentStyle = this.checkboxInactiveStyle;
    this.checkboxStyles = {
      checkboxCurrentStyle: this.checkboxInactiveStyle,
      checkboxInactiveStyle: this.checkboxInactiveStyle,
      checkboxActiveStyle: this.checkboxActiveStyle,
    };
  }

  private searchInputStyles() {
    const { iconStyle } = this.configService.getInputFieldCssStyles(this.attri.widgetProperties);

    const inputWidth = this.attri.widgetProperties?.inputFieldSettings?.css_config?.inputWidth;
    this.searchInputStyle = {
      width: inputWidth ? `${inputWidth}%` : '25%',
    };
    this.tableToolbarHeaderStyle = {
      'background-color': this.attri?.['tableToolbarHeaderBgColor'] ?? 'var(--color-surface)',
      'justify-content': this.attri.widgetProperties?.inputFieldSettings?.css_config?.inputPosition ?? 'end',
    };

    this.searchIconStyle = {
      ...iconStyle,
    };

    this.currentSearchInputStyle = this.currentInactiveStyle;
  }

  public setCssAttributes() {
    const { contentStyle, colorStyle, borderStyle } = setCssAttributes(
      this.styleService,
      this.configService,
      this.attri,
    );
    this.contentStyle = {
      ...contentStyle,
      ...borderStyle,
    };
    this.textStyle = colorStyle;
  }

  public moreOption(event: { id: string; res: any }) {
    this.isSelectedId = event.id;
    this.isShowOption = !this.isShowOption;
    this.isClickMenuIcon = true;
    const updatedMenuPopupCss = {
      ...this.menuPopupCss,
    };
    this.menuPopupCss = updatedMenuPopupCss;
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return Number(this.dataIn.attri['pageSize']);
    else return 10;
  }

  public get pagingEnabled(): boolean {
    if (this.dataIn.attri['pagingEnabled'] != null) return this.dataIn.attri['pagingEnabled'];
    else return true;
  }

  public onPageChange(event: { first: number; page: number; pageCount: number; rows: number }) {
    this.pageChange.emit(event);
  }

  public closeMenu() {
    if (this.isShowOption && !this.isClickMenuIcon) {
      this.isShowOption = false;
      this.isSelectedId = '';
    }
    this.isClickMenuIcon = false;
  }

  public goToLink(event: { link: string; id: string; actionOption: ZoneActionOptions }) {
    this.performAction.emit({
      actionId: event.id,
      actionKey: event.link,
      actionOption: event.actionOption,
    });
    this.closeMenu();
  }

  public handleAction(event: any) {
    this.actionEvent.emit(event);
  }

  public searchValue(searchValue: string) {
    this.searchValueText.emit(searchValue);
  }

  public emitSelectedItemsIds(event: string[]) {
    this.checkboxSelection.emit(event);
  }

  public onClickRowData(event: any) {
    this.rowData.emit(event);
  }
  public onDropdownValue(event: string) {
    this.dropDownValue.emit(event);
  }
  public onConfirmButtonAction(event: any) {
    this.confirmButtonAction.emit(event);
  }

  public emitSelectedItems(event: any[]) {
    this.tableHeaderCheckboxSelection.emit(event);
  }
}
